import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClickOutside } from '../../lib/useClickOutside'
import { formatMonth } from '../../lib/months'

/** The Usage page's Period filter.
 *
 * Matches the prototype's `mountMonthPicker`: the same dropdown chrome as the
 * neighbouring multi-selects, but single-select, because MCP billing is
 * monthly so a period is always exactly one calendar month — there is no
 * range to pick. Picking closes the popover, unlike the multi-selects, which
 * stay open so several boxes can be ticked in one go.
 *
 * The option list comes from the server, not from the browser clock: "which
 * month is it" is an account-timezone question (spec §24b-tz), and the
 * account's zone is Mambu-side configuration the browser cannot see. */
export function MonthPicker({
  months,
  value,
  onChange,
  ariaLabel,
}: Readonly<{
  months: string[]
  value: string | null
  onChange: (next: string) => void
  ariaLabel?: string
}>) {
  const { i18n, t } = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  useClickOutside(containerRef, () => setOpen(false), open)

  const labels = useMemo(
    () => new Map(months.map((m) => [m, formatMonth(m, i18n.language)])),
    [months, i18n.language]
  )

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        aria-expanded={open}
        disabled={months.length === 0}
        className={`w-full flex items-center justify-between gap-2 text-[12.5px] px-2.5 py-1.5 border border-[var(--border)] rounded-md bg-[var(--bg-input)] text-[var(--text-primary)] disabled:opacity-60 ${open ? 'rounded-b-none' : ''}`}
      >
        <span className="truncate">{(value && labels.get(value)) ?? t('common.dash')}</span>
        <svg
          viewBox="0 0 24 24"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 opacity-60"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          data-testid="filter-popover"
          // The prototype's `.msel-pop`: exactly the trigger's width
          // (left/right 0), stuck to it with no gap, its top corners square
          // and its bottom ones rounded, so trigger and panel read as one
          // control. Inline shadow: Tailwind reads `shadow-[…]` with a
          // colour as a shadow colour.
          className="absolute left-0 right-0 top-full -mt-px max-h-80 overflow-y-auto flex flex-col bg-[var(--bg-card)] border border-[var(--border)] rounded-b-[10px] z-50 p-2"
          style={{ boxShadow: '0 8px 28px rgba(0,0,0,.16)' }}
        >
          {months.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                onChange(m)
                setOpen(false)
              }}
              className={`text-left px-2 py-1.5 rounded-md text-[12.5px] hover:bg-[var(--bg-hover)] ${
                m === value
                  ? 'bg-[var(--bg-selected)] text-[var(--accent-text)] font-semibold'
                  : 'text-[var(--text-primary)]'
              }`}
            >
              {labels.get(m)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
